import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Trash2, FileText, Image as ImageIcon, FileArchive, File as FileGeneric, Download } from "lucide-react";
import { descargarArchivo, nombreDescarga } from "@/lib/descargas";

const ICONS = {
  image: ImageIcon,
  pdf: FileText,
  archive: FileArchive,
  default: FileGeneric,
};

function detectKind(mime, name = "") {
  const m = (mime || "").toLowerCase();
  const ext = name.split(".").pop()?.toLowerCase() || "";
  if (m.startsWith("image/")) return "image";
  if (m === "application/pdf" || ext === "pdf") return "pdf";
  if (["zip", "rar", "7z", "psd", "ai", "indd"].includes(ext)) return "archive";
  return "default";
}

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdjuntosBlock({ peticionId, adjuntos = [], onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    if (!peticionId) {
      alert("Guarda la petición antes de adjuntar archivos.");
      return;
    }
    if (file.size > 50_000_000) { alert("Archivo demasiado grande (máx 50 MB por archivo)."); return; }
    setUploading(true); setProgress(0);
    try {
      // resource_type=auto acepta imagen, PDF, raw (AI/PSD/ZIP) etc.
      const out = await uploadToCloudinary(file, "dnf/adjuntos", "auto");
      const { data } = await api.post(`/peticiones/${peticionId}/adjuntos`, {
        nombre: file.name,
        url: out.secure_url,
        tipo: file.type || out.resource_type,
        tamano: file.size,
        public_id: out.public_id,
      });
      onChange?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      alert("Error subiendo archivo: " + detail);
      console.error("[AdjuntosBlock.upload] error:", e);
    } finally { setUploading(false); setProgress(0); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await upload(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = async (adj) => {
    if (!window.confirm(`¿Borrar "${adj.nombre}"?`)) return;
    await api.delete(`/peticiones/${peticionId}/adjuntos/${adj.id}`);
    onChange?.(null, adj.id);
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 mb-4" data-testid="adjuntos-block">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5" /> Archivos adjuntos del cliente
          {adjuntos.length > 0 && <span className="text-zinc-500 normal-case tracking-normal">({adjuntos.length})</span>}
        </div>
        <Button size="sm" type="button" disabled={uploading}
          onClick={() => fileRef.current?.click()}
          data-testid="adjunto-upload-btn"
          className="h-7 px-2 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40">
          <Upload className="w-3 h-3 mr-1" /> {uploading ? "Subiendo…" : "Subir archivo"}
        </Button>
        <input ref={fileRef} type="file" multiple onChange={onFiles} className="hidden"
          accept="image/*,application/pdf,.zip,.rar,.7z,.psd,.ai,.indd,.eps,.cdr,.tif,.tiff" />
      </div>

      {adjuntos.length === 0 && !uploading && (
        <div className="text-xs text-zinc-500 italic">
          Renders, fotos del PDV, instrucciones del cliente, layouts… (PDF, imágenes, ZIP, PSD/AI). Visible para diseño, producción y montadores.
        </div>
      )}

      {adjuntos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {adjuntos.map((a) => {
            const kind = detectKind(a.tipo, a.nombre);
            const Icon = ICONS[kind] || ICONS.default;
            const isImg = kind === "image" && a.url;
            return (
              <div key={a.id} data-testid={`adjunto-${a.id}`}
                className="group relative bg-zinc-950/60 border border-zinc-800 rounded-lg p-2 hover:border-amber-500/40 transition-colors">
                {isImg ? (
                  <a href={a.url} target="_blank" rel="noreferrer" className="block aspect-video bg-zinc-900 rounded overflow-hidden">
                    <img src={cloudinaryThumb(a.url, 320, 180)} alt={a.nombre} className="w-full h-full object-cover" />
                  </a>
                ) : (
                  <a href={a.url} target="_blank" rel="noreferrer" className="flex aspect-video bg-zinc-900 rounded items-center justify-center text-zinc-400 hover:text-amber-300">
                    <Icon className="w-10 h-10" />
                  </a>
                )}
                <div className="mt-1.5">
                  <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-200 hover:text-amber-300 truncate block" title={a.nombre}>
                    {a.nombre}
                  </a>
                  <div className="text-[10px] text-zinc-500 flex items-center justify-between mt-0.5">
                    <span>{formatBytes(a.tamano)}</span>
                    {a.subido_por && <span className="truncate ml-2">por {a.subido_por}</span>}
                    <button type="button" onClick={() => descargarArchivo(a)} data-testid={`adjunto-descargar-${a.id}`}
                      title={`Descargar como «${nombreDescarga(a)}»`}
                      className="ml-2 text-amber-300 hover:text-amber-200 inline-flex items-center gap-0.5">
                      <Download className="w-3 h-3" /> Descargar
                    </button>
                  </div>
                </div>
                <button type="button" onClick={() => remove(a)}
                  data-testid={`adjunto-del-${a.id}`}
                  className="absolute top-1 right-1 bg-zinc-950/90 text-zinc-500 hover:text-red-400 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
